<script lang="ts" setup>
import OmniDialog from "@/components/OmniDialog.vue";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import type { OwnerCompany, OwnerDto } from "@/api/users/dto/owner.dto";
import TruckLink from "@/components/links/TruckLink.vue";
import RouteParticipant from "@/views/trips/view/components/RouteParticipant.vue";
import TruckStatusChip from "@/components/chips/TruckStatusChip.vue";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import OwnerCompanyShortInfo from "@/components/user/OwnerCompanyShortInfo.vue";
import { TruckStatus, TruckStatusNames } from "@/data/truck";

const api = new API();

const { t } = useI18n();
const { showAPIError } = useError();

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  title: string;
  routeDriver: DriverDto;
  routeOwner: OwnerDto;
  truck: TruckDto;
  loadingUndo: boolean;
}>();

const emits = defineEmits<{
  (e: "apply"): void;
}>();

const loadingCompanies = ref<boolean>(false);
const routeOwnerCompany = ref<OwnerCompany | null>(null);
const truckOwnerCompany = ref<OwnerCompany | null>(null);

const isDriverDiff = computed(() => {
  return props.routeDriver.id !== props.truck.driverId;
});

const isOwnerDiff = computed(() => {
  return props.routeOwner.id !== props.truck.ownerId;
});

const isOwnerCompanyDiff = computed(() => {
  return props.routeDriver.ownerCompanyId !== props.truck.driver?.ownerCompanyId;
});

const isTruckInvalidStatus = computed(() => {
  if (!props.truck.status) {
    return true;
  }

  const invalidStatus = [TruckStatus.InRoute];

  return invalidStatus.includes(props.truck.status);
});

const invalidMessage = computed(() => {
  const reasons: string[] = [];

  if (isTruckInvalidStatus.value && props.truck.status) {
    reasons.push(
      t("undo.reasons.truck-status", {
        number: props.truck.number,
        status: TruckStatusNames[props.truck.status],
      }),
    );
  }

  if (isOwnerCompanyDiff.value) {
    reasons.push(
      t("undo.reasons.owner-changed", {
        oldOwner: props.routeOwner.name,
        newOwner: props.truck.owner.name,
      }),
    );
  }

  if (isDriverDiff.value) {
    reasons.push(
      t("undo.reasons.driver-changed", {
        oldDriver: props.routeDriver.name,
        newDriver: props.truck.driver?.name,
      }),
    );
  }

  if (isOwnerCompanyDiff.value && routeOwnerCompany.value && truckOwnerCompany.value) {
    reasons.push(
      t("undo.reasons.owner-company-changed", {
        oldCompany: routeOwnerCompany.value.name,
        newCompany: truckOwnerCompany.value.name,
      }),
    );
  }

  if (reasons.length === 0) {
    return "";
  }

  return t("undo.reasons.header") + "\n" + reasons.join("\n");
});

const isDisabled = computed(() => {
  return isOwnerDiff.value || isDriverDiff.value || isOwnerCompanyDiff.value || isTruckInvalidStatus.value;
});

async function loadOwnerCompany(
  ownerId: number | undefined,
  ownerCompanyId: number | null | undefined,
): Promise<OwnerCompany | null> {
  if (!ownerId) {
    return null;
  }

  try {
    const response = await api.users.findCompanies(ownerId);

    if (response.errorData) {
      showAPIError(response);
      return null;
    }

    if (!response.data) {
      return null;
    }

    return response.data?.find((company) => company.id === ownerCompanyId) || null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

watch(
  model,
  async () => {
    loadingCompanies.value = true;
    if (!isOwnerCompanyDiff.value) {
      const company = await loadOwnerCompany(props.routeOwner.id, props.routeDriver.ownerCompanyId);
      routeOwnerCompany.value = company;
      truckOwnerCompany.value = company;
    } else {
      routeOwnerCompany.value = await loadOwnerCompany(props.routeOwner.id, props.routeDriver.ownerCompanyId);
      truckOwnerCompany.value = await loadOwnerCompany(props.truck.ownerId, props.truck.driver?.ownerCompanyId);
    }

    loadingCompanies.value = false;
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog
    v-model="model"
    :loading-companies="loadingCompanies"
    :primary-action-label="t('general.apply')"
    :primary-button-disabled="isDisabled"
    :primary-button-loader="loadingUndo"
    :title="props.title"
    :width="1000"
    @primary-action:click="emits('apply')"
  >
    <v-row>
      <v-col cols="6">
        <div class="bg-grey-lighten-4 rounded-lg pa-4">
          <truck-link
            v-if="truck.id"
            :trailer-number="truck.trailer?.number"
            :truck-id="truck.id"
            :truck-number="truck.number"
          />

          <route-participant :user="routeDriver" class="mt-4" />

          <route-participant :user="routeOwner" class="mt-3" />

          <div v-if="routeOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
            <owner-company-short-info :company="routeOwnerCompany" />
          </div>
        </div>
      </v-col>

      <v-col cols="6">
        <div class="bg-grey-lighten-4 rounded-lg pa-4">
          <div class="d-flex col-gap-3">
            <truck-link
              v-if="truck.id"
              :trailer-number="truck.trailer?.number"
              :truck-id="truck.id"
              :truck-number="truck.number"
            />

            <truck-status-chip :truck="truck" />
          </div>

          <route-participant v-if="truck.driver" :user="truck.driver" class="mt-4" />

          <route-participant v-if="truck.owner" :user="truck.owner" class="mt-3" />

          <div v-if="truckOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
            <owner-company-short-info :company="truckOwnerCompany" />
          </div>
        </div>
      </v-col>
    </v-row>

    <p class="mt-3 text-danger white-space-pre-line">
      {{ invalidMessage }}
    </p>
  </omni-dialog>
</template>
